/**
 * Restaurant settings.
 *
 * Two columns of titled cards once the window is wide enough for both. The wide
 * column carries what the customer reads — the details, the cover, the prep
 * model, the acceptance window, the block list; the narrow one carries the
 * switches and numbers a merchant changes while service is running. The split
 * is by how often a setting is touched, not by subject: pausing orders mid-rush
 * should not mean scrolling past the description and the cover to reach it.
 *
 * The prep knobs stay exposed deliberately — they are the cold-start model, and
 * for a new restaurant they are the only thing standing between a guess and a
 * useful estimate.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ActivityIndicator, View } from 'react-native';
import {
  ADDRESS_REQUIRED_MESSAGE,
  formatDayTime,
  formatDuration,
  formatMoney,
  formatMonthYear,
} from '@yummy/shared';
import {
  listBlockedNumbers,
  unblockNumber,
  type BlockedNumber,
  type MerchantRestaurant,
  fetchEmailStatus,
  fetchEtaInsights,
  fetchMerchantRestaurant,
  updateRestaurant,
  uploadMedia,
} from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  ConfirmDialog,
  ErrorBanner,
  ImagePicker,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  Stack,
  SegmentedControl,
  SwitchField,
  Text,
  TextField,
  ToneBadge,
  VerifiedBowl,
  useAction,
  useAsync,
  useResponsive,
  useT,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Dot colour for the three states the receipt pipeline can report: sending
 * without failures, sending with failures, or not configured at all.
 */
const EMAIL_DOT = {
  ok: 'bg-success',
  failed: 'bg-warning',
  off: 'bg-subtle-foreground',
} as const;

/**
 * How wide the column of frequently-changed settings is, in points.
 *
 * Wide enough for the longest field label it holds without wrapping ("Minimum
 * notice (minutes)" beside its input) and no wider, because every point it
 * takes comes out of the details column.
 */
const SIDE_COLUMN_WIDTH = 336;

/**
 * Narrowest a column of a two-field pair may be, in points.
 *
 * The floor that decides whether a pair sits side by side or stacks. Below it
 * the row wraps and each field takes a line of its own.
 *
 * The number comes from the acceptance pair, which is the demanding one: its
 * segmented control splits the column in two again, so a column narrower than
 * this leaves each segment less than the 112 points that "Order placed" needs at
 * this type size — 88 of text plus 12 of padding a side — and the label clips to
 * "Order pl…". A pair therefore needs 484 of card.
 *
 * In practice the row wraps a little before that, because the field beside it
 * has a minimum of its own; measured, the pair holds together from about 550 of
 * card and stacks below. That errs towards stacking, which costs a line and
 * never clips. The phone-and-web-address pair would be happy narrower, and
 * sharing the floor keeps one rule for the page rather than one per pair.
 */
const FIELD_PAIR_MIN_WIDTH = 234;

/**
 * Width of the order-kind column in the acceptance window's outcome list.
 *
 * A fixed column rather than a pair, because the two rows are read as a list:
 * their sentences have to start at the same x to look like the same kind of
 * statement. Wide enough for "Scheduled orders" on one line at `text-sm`.
 */
const GRACE_KIND_WIDTH = 120;

/**
 * A number field's text as a number, falling back when it is empty or
 * unparsable.
 *
 * Shared by the save path and the acceptance window's outcome list, so the list
 * can never state a figure the save would not write.
 */
function fieldNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * The window width at which the page splits into two columns.
 *
 * Set to the kit's widest content column (`max-w-6xl`, 1152) rather than to a
 * device breakpoint: below it, the side column and its gutter come out of a
 * content column that is still narrower than the design allows, and the wide
 * column ends up the smaller of the two. Measured at 1024 the wide column was
 * 332 points against a 336-point side column — narrower than the thing beside
 * it, and too narrow to hold two fields side by side.
 *
 * Above it the two columns have room. At 1152 the wide column is 460 points, so
 * a pair of prep fields is still 222 each.
 */
const TWO_COLUMN_MIN_WIDTH = 1152;

/**
 * One settings group as a card.
 *
 * The heading sits inside the card rather than above it because a settings page
 * is scanned as a set of objects — you look for the card you came for — not read
 * as a run of headings down an open page. It also keeps a group's title, its
 * explanation and its fields inside one border when the groups are laid out
 * side by side, where a heading floating above a card would read as belonging to
 * whichever card is nearest.
 *
 * The title is optional. A card of switches whose own labels already say what
 * they are carries a heading that only repeats them, and a heading that repeats
 * its fields costs a line and teaches nothing.
 *
 * The title is given `aria-level` 4 so the outline runs page title, then group,
 * rather than announcing every card at the same level as the page.
 *
 * @param props Title, explanation, and the group's fields.
 * @returns The card.
 */
function SettingsCard({
  title,
  description,
  children,
}: {
  /** Omitted for a card that does not need naming. */
  title?: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      {title || description ? (
        <CardHeader>
          {title ? (
            <CardTitle className="text-base" aria-level={4}>
              {title}
            </CardTitle>
          ) : null}
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
      ) : null}
      <CardContent>
        <Stack gap="lg">{children}</Stack>
      </CardContent>
    </Card>
  );
}

/**
 * The unit for a number field, set inside the box beside the value.
 *
 * One convention for the whole page: the label names the quantity, the box
 * carries the unit. Half the page used to carry it in the label —
 * "Minimum notice (minutes)" — while the waiting-message field carried it in
 * the box, which is two conventions for one thing, and the label is the more
 * expensive place to put it: "(minutes)" is a word that has to fit a column and
 * be translated, where "min" sits in space the box already has.
 *
 * @param props The unit text, already translated.
 * @returns The unit as it appears inside a field.
 */
function Unit({ children }: { children: string }) {
  return <Text className="text-subtle-foreground text-sm">{children}</Text>;
}

/**
 * Every value the settings form holds, as the strings the controls carry.
 *
 * Strings throughout, including the numbers, because that is what a `TextField`
 * gives back — comparing a typed `'15'` against a stored `15` would report a
 * change on every field the moment the form was filled.
 */
interface SettingsForm {
  name: string;
  description: string;
  phone: string;
  address: string;
  accepting_orders: boolean;
  auto_accept_orders: boolean;
  currency: string;
  tax_rate_percent: string;
  min_lead_minutes: string;
  max_advance_days: string;
  slot_interval_minutes: string;
  default_prep_minutes: string;
  min_prep_minutes: string;
  max_prep_minutes: string;
  kitchen_capacity: string;
  accept_grace_minutes: string;
  accept_grace_basis: string;
  ready_chase_minutes: string;
  cook_round_minutes: string;
}

/** The form before anything is loaded, and the shape a fresh restaurant edits. */
const EMPTY_FORM: SettingsForm = {
  name: '',
  description: '',
  phone: '',
  address: '',
  accepting_orders: true,
  auto_accept_orders: false,
  currency: 'SGD',
  tax_rate_percent: '0',
  min_lead_minutes: '10',
  max_advance_days: '7',
  slot_interval_minutes: '15',
  default_prep_minutes: '15',
  min_prep_minutes: '5',
  max_prep_minutes: '90',
  kitchen_capacity: '3',
  accept_grace_minutes: '5',
  accept_grace_basis: 'placed',
  ready_chase_minutes: '10',
  cook_round_minutes: '10',
};

/**
 * The form's values for a restaurant as stored.
 *
 * Used twice — to fill the form, and to tell whether what is on screen still
 * matches what was loaded — and deliberately only once in the source. A
 * comparison written separately from the thing it compares drifts, and a Save
 * button that greys out over a value nobody touched is worse than one that
 * never greys out at all.
 *
 * @param row The restaurant as loaded.
 * @returns The form values it corresponds to.
 */
function formFrom(row: MerchantRestaurant): SettingsForm {
  return {
    name: row.name,
    description: row.description ?? '',
    phone: row.phone ?? '',
    address: row.address ?? '',
    accepting_orders: row.accepting_orders,
    auto_accept_orders: row.auto_accept_orders,
    currency: row.currency,
    tax_rate_percent: (row.tax_rate_bps / 100).toString(),
    min_lead_minutes: String(row.min_lead_minutes),
    max_advance_days: String(row.max_advance_days),
    slot_interval_minutes: String(row.slot_interval_minutes),
    default_prep_minutes: String(row.default_prep_minutes),
    min_prep_minutes: String(row.min_prep_minutes),
    max_prep_minutes: String(row.max_prep_minutes),
    accept_grace_minutes: String(Math.round(row.accept_grace_seconds / 60)),
    accept_grace_basis: row.accept_grace_basis,
    ready_chase_minutes: String(Math.round(row.ready_chase_seconds / 60)),
    cook_round_minutes: String(Math.round(row.cook_round_seconds / 60)),
    kitchen_capacity: String(row.kitchen_capacity),
  };
}

/**
 * Renders the restaurant settings form.
 *
 * @returns The settings screen.
 */
export function SettingsScreen() {
  const theme = useTheme();
  const t = useT();
  const toast = useToast();
  const { restaurant, refreshRestaurants, logoutEverywhere } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  // Read here rather than at the point of use: the layout decides itself before
  // any of the screen's early returns, because a hook cannot be called after one.
  const { width, isTablet } = useResponsive();
  const twoColumn = width >= TWO_COLUMN_MIN_WIDTH;

  const data = useAsync(
    async () => (restaurantId ? fetchMerchantRestaurant(supabase, restaurantId) : null),
    [restaurantId],
  );

  const insights = useAsync(
    async () => (restaurantId ? fetchEtaInsights(supabase, restaurantId) : null),
    [restaurantId],
  );

  const email = useAsync(
    async () => (restaurantId ? fetchEmailStatus(supabase, restaurantId) : null),
    [restaurantId],
  );

  const action = useAction();

  /**
   * Numbers this restaurant has blocked.
   *
   * Kept in local state rather than the shared async hook so that unblocking
   * removes the row at once — the call has already succeeded by then, and
   * waiting for a refetch makes a merchant tap twice.
   */
  const [blockedNumbers, setBlockedNumbers] = useState<BlockedNumber[]>([]);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  /**
   * The one action here that reaches devices the merchant is not holding.
   *
   * Behind a confirmation because it ends this session too, and the merchant has
   * to be told that the other devices are not cut off at once: their access
   * tokens stay valid until they next refresh.
   */
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const loadBlocked = useCallback(async () => {
    if (!restaurantId) return;
    try {
      setBlockedNumbers(await listBlockedNumbers(supabase, restaurantId));
    } catch {
      // Nothing to say: an empty list is indistinguishable from a failed read,
      // and neither should stop a merchant using the rest of the screen.
    }
  }, [restaurantId]);

  useEffect(() => {
    void loadBlocked();
  }, [loadBlocked]);

  const unblock = useCallback(
    async (phone: string) => {
      if (!restaurantId) return;
      setUnblocking(phone);
      try {
        await unblockNumber(supabase, restaurantId, phone);
        setBlockedNumbers((current) => current.filter((entry) => entry.phone !== phone));
        toast.show(t('merchant.settings.unblockedToast'), { tone: 'success' });
      } catch {
        toast.show(t('merchant.settings.unblockFailedToast'), { tone: 'danger' });
      } finally {
        setUnblocking(null);
      }
    },
    [restaurantId, toast, t],
  );

  /**
   * Ends every session the account has, this device included.
   *
   * No success toast: this screen is gone by then — the sign-in screen is the
   * receipt. Only the failure needs saying, because nothing else would happen.
   */
  const signOutEverywhere = useCallback(async () => {
    setSigningOut(true);
    try {
      await logoutEverywhere();
    } catch {
      setSigningOut(false);
      setConfirmingSignOut(false);
      toast.show(t('merchant.settings.signOutEverywhereFailed'), { tone: 'danger' });
    }
  }, [logoutEverywhere, toast, t]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | Blob | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  useEffect(() => {
    const row = data.data;
    if (!row) return;
    setForm(formFrom(row));
  }, [data.data]);

  const set = useCallback(<K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    if (!restaurantId) return;

    // A restaurant must keep an address: it is what the customer's order tells
    // them to walk to, and the database holds every write to that. Checking here
    // is what turns the refusal into a sentence beside the field.
    if (form.address.trim() === '') {
      setAddressError(ADDRESS_REQUIRED_MESSAGE);
      return;
    }
    setAddressError(null);

    const minPrep = Math.max(0, Math.trunc(fieldNumber(form.min_prep_minutes, 5)));
    const maxPrep = Math.max(minPrep, Math.trunc(fieldNumber(form.max_prep_minutes, 90)));

    const result = await action.run(async () => {
      let coverUrl = data.data?.cover_url ?? null;

      if (coverFile) {
        const uploaded = await uploadMedia(supabase, restaurantId, coverFile, 'brand');
        coverUrl = uploaded.publicUrl;
      }

      return updateRestaurant(supabase, restaurantId, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim(),
        accepting_orders: form.accepting_orders,
        auto_accept_orders: form.auto_accept_orders,
        currency: form.currency,
        tax_rate_bps: Math.max(0, Math.min(10000, Math.round(fieldNumber(form.tax_rate_percent, 0) * 100))),
        min_lead_minutes: Math.max(0, Math.trunc(fieldNumber(form.min_lead_minutes, 10))),
        max_advance_days: Math.max(1, Math.trunc(fieldNumber(form.max_advance_days, 7))),
        slot_interval_minutes: Math.max(5, Math.trunc(fieldNumber(form.slot_interval_minutes, 15))),
        default_prep_minutes: Math.max(1, Math.trunc(fieldNumber(form.default_prep_minutes, 15))),
        min_prep_minutes: minPrep,
        max_prep_minutes: maxPrep,
        accept_grace_seconds: Math.min(
          3600,
          Math.max(0, Math.trunc(fieldNumber(form.accept_grace_minutes, 5))) * 60,
        ),
        accept_grace_basis: form.accept_grace_basis === 'estimate' ? 'estimate' : 'placed',
        ready_chase_seconds: Math.min(
          3600,
          Math.max(0, Math.trunc(fieldNumber(form.ready_chase_minutes, 10))) * 60,
        ),
        // A round has to have a length: the column refuses anything under a
        // minute, so the form clamps rather than letting the save fail.
        cook_round_seconds: Math.min(
          3600,
          Math.max(1, Math.trunc(fieldNumber(form.cook_round_minutes, 10))) * 60,
        ),
        kitchen_capacity: Math.max(1, Math.trunc(fieldNumber(form.kitchen_capacity, 3))),
        cover_url: coverUrl,
      });
    });

    if (result) {
      setCoverFile(null);
      toast.show(t('merchant.settings.saved'), { tone: 'success' });
      data.reload();
      await refreshRestaurants();
    }
  }, [restaurantId, form, coverFile, data, action, toast, refreshRestaurants, t]);

  if (!restaurantId) {
    return (
      <Screen>
        <Text className="text-2xl font-bold">{t('merchant.settings.noRestaurant')}</Text>
      </Screen>
    );
  }

  if (data.initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.settings.loading')} />
      </Screen>
    );
  }

  if (data.error || !data.data) {
    return (
      <Screen>
        <ErrorBanner message={data.error ?? t('merchant.settings.loadFailed')} onRetry={data.reload} />
      </Screen>
    );
  }

  const row = data.data;
  const currency = row.currency;
  const samplePrice = 1000;
  const taxPercent = Number(form.tax_rate_percent) || 0;

  /**
   * Whether anything on screen differs from what was loaded.
   *
   * Compared field by field against the row as stored, through the same mapping
   * that filled the form, so a value the form rewrote on the way in — `7` for
   * `"7"`, `0` for `"0"` — is not mistaken for an edit.
   *
   * A chosen cover counts even before it is uploaded: it is a change waiting to
   * be saved, and the file is the one edit the fields cannot show.
   */
  const loaded = formFrom(row);
  const dirty =
    coverFile !== null ||
    (Object.keys(loaded) as Array<keyof SettingsForm>).some((key) => form[key] !== loaded[key]);

  /**
   * Why a number was blocked, as a merchant reads it.
   *
   * The column stores the code the cancel flow wrote, and printing it raw put
   * `no_show · Blocked today` in front of a merchant. The three labels are the
   * ones the cancel sheet already offers, so one reason has one name across the
   * app rather than a code here and a phrase there.
   *
   * A value that matches none of them is shown as stored. It came from
   * somewhere, and a row that says nothing about why is worse than one that says
   * it in the database's own words.
   */
  const blockReason = (reason: string | null): string => {
    switch (reason) {
      case 'no_show':
        return t('merchant.orders.reasonNoShow');
      case 'sold_out':
        return t('merchant.orders.reasonSoldOut');
      case 'other':
        return t('merchant.orders.reasonOther');
      case null:
        return '';
      default:
        return reason;
    }
  };
  const emailStatus: keyof typeof EMAIL_DOT = !email.data?.configured
    ? 'off'
    : email.data.failed > 0
      ? 'failed'
      : 'ok';

  /**
   * Whether the prep ceiling is what every estimate is currently saying.
   *
   * `insights` is already loaded on this screen, and `p90_prep_seconds` is the
   * merchant's own slow tail — the number the dashboard prints as "Slowest 10%".
   * When it sits above the ceiling the model is not allowed to report what the
   * kitchen actually does: every estimate comes back at the cap, and the
   * checkout's "too soon" warning stops firing for the orders that need it most.
   * Nothing in a field hinted "no estimate is ever longer than this" suggests
   * that, which is the whole reason for the note below.
   */
  const capMinutes = Math.max(0, Math.trunc(fieldNumber(form.max_prep_minutes, 90)));
  const capBinds = insights.data !== null && insights.data.p90_prep_seconds > capMinutes * 60;

  /**
   * The four prep numbers, two to a row.
   *
   * Built as explicit rows rather than one wrapping row. A wrapping row of
   * fields with a 200-point basis sits two-up at the iPad width and three-up
   * once the window is wide, which orphans the fourth field across the whole
   * width of the card — the count is a property of the window rather than of
   * the design. Pairing them here makes two the only count the layout can
   * produce, at any width.
   *
   * One column on a phone, where a pair would be about 140 points of field each
   * and every label in the card would wrap to three lines.
   */
  const prepFields = [
    <TextField
      key="overhead"
      label={t('merchant.settings.overhead')}
      value={form.default_prep_minutes}
      onChangeText={(value) => set('default_prep_minutes', value)}
      keyboardType="number-pad"
      hint={t('merchant.settings.overheadHint')}
      suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
    />,
    <TextField
      key="fastest"
      label={t('merchant.settings.fastest')}
      value={form.min_prep_minutes}
      onChangeText={(value) => set('min_prep_minutes', value)}
      keyboardType="number-pad"
      hint={t('merchant.settings.fastestHint')}
      suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
    />,
    <TextField
      key="slowest"
      label={t('merchant.settings.slowest')}
      value={form.max_prep_minutes}
      onChangeText={(value) => set('max_prep_minutes', value)}
      keyboardType="number-pad"
      // Interpolated because the sentence now says what a capped order reads:
      // the very number the merchant is looking at.
      hint={t('merchant.settings.slowestHint', { minutes: capMinutes })}
      suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
    />,
    <TextField
      key="ordersAtOnce"
      label={t('merchant.settings.ordersAtOnce')}
      value={form.kitchen_capacity}
      onChangeText={(value) => set('kitchen_capacity', value)}
      keyboardType="number-pad"
      hint={t('merchant.settings.ordersAtOnceHint')}
    />,
  ];
  const prepRows = isTablet ? [prepFields.slice(0, 2), prepFields.slice(2)] : prepFields.map((field) => [field]);

  /**
   * The switches a merchant reaches for while service is running, chiefly
   * pausing orders mid-rush.
   *
   * Written once and placed by width, rather than listed in the right-hand
   * column in both layouts. Stacked into one column it has to sit up near the
   * top — behind the details, the cover and the prep model it is a long scroll
   * during the rush it exists for — while beside the main column it belongs
   * with the other running-service settings. Placing it by width rather than
   * reordering with `order` keeps the tab order and the paint order the same
   * thing in both layouts, which a CSS reorder would not.
   */
  const takingOrdersCard = (
    <SettingsCard>
      {!form.accepting_orders ? (
        <Callout
          tone="warning"
          title={t('merchant.settings.paused')}
          message={t('merchant.settings.pausedHint')}
        />
      ) : null}

      <SwitchField
        label={t('merchant.settings.acceptOrders')}
        description={t('merchant.settings.acceptOrdersHint')}
        value={form.accepting_orders}
        onValueChange={(value) => set('accepting_orders', value)}
      />

      <SwitchField
        label={t('merchant.settings.autoAccept')}
        description={t('merchant.settings.autoAcceptHint')}
        value={form.auto_accept_orders}
        onValueChange={(value) => set('auto_accept_orders', value)}
      />
    </SettingsCard>
  );

  /**
   * The block list, which goes last once the cards are stacked.
   *
   * The least-read card on the page: a number is blocked from the order that
   * went wrong, and unblocked rarely, so stacked it belongs below the receipts
   * rather than in the middle of the settings a merchant changes while service
   * is running. Beside the main column it keeps the place it had, because the
   * side column is already the foot of the page there.
   *
   * Written once and placed by width, like the switches above, so the tab order
   * and the paint order stay the same thing in both layouts.
   */
  const blockedCard = (
    <SettingsCard
      title={t('merchant.settings.blockedTitle')}
      description={t('merchant.settings.blockedHint')}>
      {blockedNumbers.length === 0 ? (
        <Text className="text-muted-foreground">{t('merchant.settings.blockedEmpty')}</Text>
      ) : (
        <Stack gap="md">
          {blockedNumbers.map((entry) => {
            const reason = blockReason(entry.reason);
            return (
              // Outlined like the fields above it, so one number reads as one
              // item in a list rather than as loose text the button happens to
              // sit beside. The border is the only thing separating the rows
              // once there is more than one.
              <Row
                key={entry.id}
                gap="md"
                align="center"
                spread
                className="border-input rounded-md border px-4 py-3">
                <Stack gap="xs" className="min-w-0 flex-1">
                  <Text className="text-base">{entry.phone}</Text>
                  <Text className="text-subtle-foreground text-xs">
                    {reason ? `${reason} · ` : ''}
                    {t('merchant.settings.blockedSince', {
                      date: formatDayTime(entry.blocked_at, row.timezone, undefined, t),
                    })}
                  </Text>
                </Stack>
                <Button
                  variant="outline"
                  onPress={() => void unblock(entry.phone)}
                  disabled={unblocking === entry.phone}
                  accessibilityLabel={`${t('merchant.settings.unblock')} ${entry.phone}`}>
                  <Text>{t('merchant.settings.unblock')}</Text>
                </Button>
              </Row>
            );
          })}
        </Stack>
      )}
    </SettingsCard>
  );

  /**
   * Pickup times and Money, placed after the cover once the cards stack.
   *
   * Both are read early — when customers may collect, and what they pay —
   * so stacked they follow the two cards that say who the restaurant is,
   * rather than sitting below the prep model. Beside the main column they
   * keep the place they had, which is the foot of the page. Placed by width,
   * like the switches and the block list, so the tab order and the paint
   * order stay the same thing in both layouts.
   */
  /**
   * The four pickup fields, two to a row once the card has the width.
   *
   * Two up only outside the side column. In there the card is 336 points and a
   * pair would leave each field about 140 — narrower than the zone name the
   * last one has to show. Stacked on the page the card is 484 or more and the
   * pair fits, which is worth a row: the zone hint runs to four lines, so the
   * card is tall enough that halving it is the difference between reading it
   * whole and scrolling.
   *
   * Explicit rows rather than one wrapping row, like the prep card: four fields
   * with a basis will fit three across on a wide card and orphan the fourth.
   */
  const pickupFields = [
    <TextField
      key="minNotice"
      label={t('merchant.settings.minNotice')}
      value={form.min_lead_minutes}
      onChangeText={(value) => set('min_lead_minutes', value)}
      keyboardType="number-pad"
      hint={t('merchant.settings.minNoticeHint')}
      suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
    />,
    <TextField
      key="bookAhead"
      label={t('merchant.settings.bookAhead')}
      value={form.max_advance_days}
      onChangeText={(value) => set('max_advance_days', value)}
      keyboardType="number-pad"
      suffix={<Unit>{t('merchant.settings.daysUnit')}</Unit>}
    />,
    <TextField
      key="slotSpacing"
      label={t('merchant.settings.slotSpacing')}
      value={form.slot_interval_minutes}
      onChangeText={(value) => set('slot_interval_minutes', value)}
      keyboardType="number-pad"
      hint={t('merchant.settings.slotSpacingHint')}
      suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
    />,
    // Shown for the same reason the web address is: a merchant asks why the
    // pickups look wrong, and the answer is the zone.
    <TextField
      key="timezone"
      label={t('merchant.settings.timezone')}
      value={row.timezone}
      onChangeText={() => undefined}
      disabled
      hint={t('merchant.settings.timezoneHint')}
    />,
  ];
  const pickupRows =
    isTablet && !twoColumn
      ? [pickupFields.slice(0, 2), pickupFields.slice(2)]
      : pickupFields.map((field) => [field]);

  const pickupTimesCard = (
    <SettingsCard
      title={t('merchant.settings.pickupTimes')}
      description={t('merchant.settings.pickupTimesHint')}>
      <Stack gap="lg">
        {pickupRows.map((fields, index) => (
          <Row key={index} gap="lg" align="start">
            {fields.map((field, position) => (
              <View key={position} className="min-w-0 flex-1">
                {field}
              </View>
            ))}
          </Row>
        ))}
      </Stack>
    </SettingsCard>
  );

  const moneyCard = (
    <SettingsCard
      title={t('merchant.settings.money')}
      description={t('merchant.settings.moneyHint')}>
      {/* A pair, laid out by the same floor the other two-field rows use: in the
          side column there is room for one field and the row wraps, and stacked
          on the page there is room for two, which saves a third of the card's
          height. No width test of its own — the column it lands in decides. */}
      <Row gap="lg" wrap align="start">
        <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
          {/* Shown, not editable, for the same reason as the time zone: it is
              decided when the restaurant is created, and changing it here would
              restate every price in the new currency without converting it — a
              SGD 10 dish becomes an A$10 dish.

              The rule is this screen's alone. Nothing below it refuses the
              change, so a caller that is not this screen still can. See
              `docs/known-issues.md`. */}
          <TextField
            label={t('common.currency')}
            value={form.currency}
            onChangeText={() => undefined}
            disabled
            hint={t('merchant.settings.currencyHint')}
          />
        </View>

        <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
          {/* The hint carries the worked example as well as the rule, so the
              control keeps one description rather than two unrelated ones. */}
          <TextField
            label={t('merchant.settings.taxRate')}
            value={form.tax_rate_percent}
            onChangeText={(value) => set('tax_rate_percent', value)}
            keyboardType="decimal-pad"
            suffix={<Unit>{t('merchant.settings.percentUnit')}</Unit>}
            hint={`${t('merchant.settings.taxRateHint')} ${t('merchant.settings.taxExample', {
              price: formatMoney(samplePrice, currency),
              total: formatMoney(
                samplePrice + Math.round((samplePrice * Math.round(taxPercent * 100)) / 10000),
                currency,
              ),
            })}`}
          />
        </View>
      </Row>
    </SettingsCard>
  );

  // The outcome list in the acceptance card states the figure the merchant is
  // typing rather than the saved one, so it moves with the field.
  const acceptGraceMinutes = Math.max(0, Math.trunc(fieldNumber(form.accept_grace_minutes, 5)));
  const chaseMinutes = Math.max(0, Math.trunc(fieldNumber(form.ready_chase_minutes, 10)));
  // Floored at a minute, matching what the save sends and what the column allows.
  const roundMinutes = Math.max(1, Math.trunc(fieldNumber(form.cook_round_minutes, 10)));

  return (
    <Screen
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            <Button
              variant="secondary"
              onPress={data.reload}
              disabled={action.pending}
              accessibilityLabel={t('merchant.settings.discard')}>
              <Text>{t('merchant.settings.discard')}</Text>
            </Button>
            <Button
              onPress={() => void save()}
              disabled={action.pending || !dirty}
              accessibilityLabel={t('merchant.settings.save')}
              aria-busy={action.pending}>
              {action.pending ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{t('merchant.settings.save')}</Text>
              )}
            </Button>
          </Row>
        </FooterBar>
      }>
      {/* No subtitle. It carried the web address and the time zone, and both are
          already on the page as fields — the address in Details, the zone in
          Pickup times — so the line under the title was the same two facts
          twice, in the one place a merchant is not reading them. */}
      <PageHeading title={t('merchant.settings.title')} />

      {action.error ? <ErrorBanner message={action.error} onRetry={action.clearError} /> : null}

      <View className={cn('w-full gap-6', twoColumn && 'flex-row items-start')}>
        {/* What the customer reads, and the model behind the estimate they are
            quoted. Long enough to need its own scroll, so it leads. */}
        <Stack gap="xl" className={cn(twoColumn ? 'min-w-0 flex-1' : 'w-full')}>
          <SettingsCard
            title={t('merchant.settings.details')}
            description={t('merchant.settings.detailsHint')}>
            <TextField
              label={t('merchant.settings.restaurantName')}
              required
              value={form.name}
              onChangeText={(value) => set('name', value)}
            />
            <TextField
              label={t('common.description')}
              value={form.description}
              onChangeText={(value) => set('description', value)}
              multiline
              numberOfLines={3}
            />
            {/* Phone and the web address share a line; the address keeps one of
                its own. The address is the longest value on the card and the one
                a customer walks to, so it is the field that must not be squeezed
                into half a row.

                `align="start"` because their hints are different lengths:
                centred — the row's default — the shorter column drops to sit
                level with the taller one and the two labels stop sharing a
                line. */}
            <Row gap="lg" wrap align="start">
              <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
                <TextField
                  label={t('common.phone')}
                  value={form.phone}
                  onChangeText={(value) => set('phone', value)}
                  keyboardType="phone-pad"
                  hint={t('merchant.settings.phoneHint')}
                />
              </View>
              <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
                {/* Shown, not editable: the address is in links customers
                    already have, and the update payload cannot carry it. */}
                <TextField
                  label={t('merchant.settings.webAddress')}
                  value={row.slug}
                  onChangeText={() => undefined}
                  disabled
                  hint={t('merchant.settings.webAddressHint')}
                />
              </View>
            </Row>
            <TextField
              label={t('common.address')}
              required
              value={form.address}
              onChangeText={(value) => set('address', value)}
              error={addressError}
              hint={t('merchant.settings.addressHint')}
            />
          </SettingsCard>

          {/* Its own card rather than the last field of Details: the cover is
              the one thing here that is a picture rather than a fact about the
              restaurant, and it is chosen from a file picker rather than typed.
              Kept apart, a merchant changing the picture is not scrolling past
              the address to find it. */}
          <SettingsCard
            title={t('merchant.settings.cover')}
            description={t('merchant.settings.coverHint')}>
            <ImagePicker
              currentUrl={coverPreview ?? row.cover_url}
              label={t('merchant.settings.coverImage')}
              aspectRatio={2.5}
              onPick={(image) => {
                setCoverFile(image.file);
                setCoverPreview(image.previewUrl);
              }}
              disabled={action.pending}
            />
          </SettingsCard>

          {/* Stacked, these three follow the cover, in the order they are read:
              what customers pay, then the switches that stop orders, then when
              they may collect. */}
          {twoColumn ? null : moneyCard}

          {twoColumn ? null : takingOrdersCard}

          {twoColumn ? null : pickupTimesCard}

          <SettingsCard
            title={t('merchant.settings.prepEstimates')}
            description={t('merchant.settings.prepEstimatesHint')}>
            {insights.data ? (
              <Row spread gap="md" align="center" wrap>
                <Text className="text-muted-foreground">
                  {t('merchant.settings.learnedFrom', { count: insights.data.sample_count })}
                </Text>
                {insights.data.is_learning ? (
                  <ToneBadge label={t('merchant.settings.stillLearning')} tone="info" size="sm" />
                ) : (
                  <ToneBadge
                    label={t('merchant.settings.medianPrep', {
                      duration: formatDuration(insights.data.median_prep_seconds, t),
                    })}
                    tone="success"
                    size="sm"
                  />
                )}
              </Row>
            ) : null}

            {/* The ceiling is what every estimate is saying. Amber rather than a
                field error: nothing is invalid — the number is merely lying —
                and raising it clears this by itself, so there is nothing to
                dismiss. */}
            {capBinds ? (
              <Callout
                tone="warning"
                title={t('merchant.settings.capBindsTitle', { cap: capMinutes })}
                message={t('merchant.settings.capBindsHint', { cap: capMinutes })}
              />
            ) : null}

            <Stack gap="lg">
              {prepRows.map((fields, index) => (
                // `align="start"` so the two fields of a row share a top edge.
                // Centred — the row's default — the field with the shorter hint
                // drops to sit level with the taller one, so "Orders at a time"
                // sat a few points above "Slowest possible" beside it and the
                // pair read as misaligned.
                <Row key={index} gap="lg" align="start">
                  {fields.map((field, position) => (
                    <View key={position} className="min-w-0 flex-1">
                      {field}
                    </View>
                  ))}
                </Row>
              ))}
            </Stack>

            <Callout
              tone="info"
              title={t('merchant.settings.perDishInfo')}
              message={t('merchant.settings.perDishInfoHint')}
            />
          </SettingsCard>

          {/* Its own card rather than two more fields in the grid above: this is
              not another number the estimate is built from, it is what the
              customer is told when the estimate is not being honoured. */}
          <SettingsCard
            title={t('merchant.settings.tellingTitle')}
            description={t('merchant.settings.tellingHint')}>
            {/* Two columns, as the drawing has them. That only holds together
                because the label is short: a pair gives each side of a 500-point
                card about 240, and a sentence-long label wraps to two lines
                there while "Counted from" stays on one, so the two controls
                would start at different heights and the pair would read as
                broken. The unit moved out of the label and into the box beside
                the number for the same reason — it is what makes the label fit a
                column at all.

                Both sides are built the same way on purpose: the same label
                markup and the same four-point gap the `Field` uses, so the two
                labels share a baseline and the two controls share a top edge. */}
            <Row gap="lg" wrap>
              <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
                <TextField
                  label={t('merchant.settings.acceptGrace')}
                  value={form.accept_grace_minutes}
                  onChangeText={(value) => set('accept_grace_minutes', value)}
                  keyboardType="number-pad"
                  suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
                />
              </View>
              <View className="flex-1" style={{ minWidth: FIELD_PAIR_MIN_WIDTH }}>
                <Stack gap="xs">
                  <Text className="text-muted-foreground text-sm font-semibold leading-none">
                    {t('merchant.settings.acceptGraceBasis')}
                  </Text>
                  <SegmentedControl
                    options={[
                      { value: 'placed', label: t('merchant.settings.acceptGracePlaced') },
                      { value: 'estimate', label: t('merchant.settings.acceptGraceEstimate') },
                    ]}
                    value={form.accept_grace_basis}
                    onChange={(value) => set('accept_grace_basis', value)}
                    fullWidth
                  />
                </Stack>
              </View>
            </Row>
            {/* The outcome per order kind, rather than a note about the control.
                The basis above applies to new orders only: a scheduled order's
                clock always starts at its pickup time, whichever basis is set.
                Stating both in the same shape is what makes that visible, where
                the rule used to be the quietest line on the card — read, after
                choosing "Order placed", as though it governed everything.

                `muted-foreground` and not `subtle-foreground`: the latter is
                2.6:1 on a white card, under AA at this size. */}
            <Stack gap="sm" className="border-border border-t pt-4">
              <Row gap="md" align="baseline">
                <Text
                  className="text-foreground text-sm font-semibold"
                  style={{ width: GRACE_KIND_WIDTH }}>
                  {t('merchant.settings.acceptGraceNewOrders')}
                </Text>
                {/* Polite, and the same node either way: the sentence is
                    replaced when the basis changes, so a screen reader hears the
                    setting it just moved to rather than two announcements. */}
                <Text className="text-muted-foreground flex-1 text-sm" aria-live="polite">
                  {form.accept_grace_basis === 'estimate'
                    ? t('merchant.settings.acceptGraceOutcomeEstimate', {
                        minutes: acceptGraceMinutes,
                      })
                    : t('merchant.settings.acceptGraceOutcomePlaced', {
                        minutes: acceptGraceMinutes,
                      })}
                </Text>
              </Row>
              <Row gap="md" align="baseline">
                <Text
                  className="text-foreground text-sm font-semibold"
                  style={{ width: GRACE_KIND_WIDTH }}>
                  {t('merchant.settings.acceptGraceScheduledOrders')}
                </Text>
                <Text className="text-muted-foreground flex-1 text-sm">
                  {t('merchant.settings.acceptGraceOutcomeScheduled', {
                    minutes: acceptGraceMinutes,
                  })}
                </Text>
              </Row>
            </Stack>
          </SettingsCard>

          {/* Its own card, and not a third field in the one above: that card is
              about what the *customer* is told, and this one is about when the
              counter is told to act. The two windows are unrelated — a shop can
              want customers reassured quickly and its own chase left alone. */}
          <SettingsCard
            title={t('merchant.settings.chaseTitle')}
            description={t('merchant.settings.chaseHint')}>
            <Stack gap="sm">
              <TextField
                label={t('merchant.settings.chaseWindow')}
                value={form.ready_chase_minutes}
                onChangeText={(value) => set('ready_chase_minutes', value)}
                keyboardType="number-pad"
                suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
              />
              <Text className="text-muted-foreground text-sm">
                {t('merchant.settings.chaseOutcome', { minutes: chaseMinutes })}
              </Text>
            </Stack>
          </SettingsCard>

          {/* The third timing knob, and the only one the kitchen reads rather
              than the counter: the grace and the chase decide what the customer
              and the board are told, this decides how much food the To cook page
              asks the kitchen to start at once. */}
          <SettingsCard
            title={t('merchant.settings.cookRoundTitle')}
            description={t('merchant.settings.cookRoundHint')}>
            <Stack gap="sm">
              <TextField
                label={t('merchant.settings.cookRoundWindow')}
                value={form.cook_round_minutes}
                onChangeText={(value) => set('cook_round_minutes', value)}
                keyboardType="number-pad"
                suffix={<Unit>{t('merchant.settings.minutesUnit')}</Unit>}
              />
              <Text className="text-muted-foreground text-sm">
                {t('merchant.settings.cookRoundOutcome', { minutes: roundMinutes })}
              </Text>
            </Stack>
          </SettingsCard>

          {/* Last in the stack, so it moves out of the main column there. */}
          {twoColumn ? blockedCard : null}
        </Stack>

        {/* Changed while service is running: pausing orders mid-rush should not
            mean scrolling past the description and the cover to reach the
            switch. A plain `View` rather than `Stack`, because the width is a
            measured number rather than a class: Tailwind reads class names from
            source text, and `SIDE_COLUMN_WIDTH` is the one place it is decided. */}
        <View
          className={cn('flex-col gap-6', twoColumn ? 'shrink-0' : 'w-full')}
          style={twoColumn ? { width: SIDE_COLUMN_WIDTH } : undefined}>
          {twoColumn ? takingOrdersCard : null}

          {twoColumn ? pickupTimesCard : null}

          {twoColumn ? moneyCard : null}

          <SettingsCard
            title={t('merchant.settings.receipts')}
            description={t('merchant.settings.receiptsHint')}>
            {email.data ? (
              <Stack gap="lg">
                <Row spread gap="md" wrap>
                  <Row gap="sm" align="center">
                    <View className={cn('h-2 w-2 rounded-full', EMAIL_DOT[emailStatus])} />
                    <Text className="text-muted-foreground">
                      {email.data.configured
                        ? t('merchant.settings.emailActive')
                        : t('merchant.settings.emailNotConfigured')}
                    </Text>
                  </Row>
                  <Text className="font-bold">
                    {t('merchant.settings.sentCount', { count: email.data.sent })}
                  </Text>
                </Row>

                {!email.data.configured ? (
                  <Callout
                    tone="info"
                    title={t('merchant.settings.receiptsQueued')}
                    message={t('merchant.settings.receiptsQueuedHint', {
                      count: email.data.queued + email.data.skipped,
                    })}
                  />
                ) : email.data.failed > 0 ? (
                  <Callout
                    tone="warning"
                    title={t('merchant.settings.receiptsFailed', { count: email.data.failed })}
                    message={email.data.last_error ?? t('merchant.settings.receiptsFailedHint')}
                  />
                ) : (
                  <Text className="text-subtle-foreground text-xs">
                    {email.data.last_sent_at
                      ? t('merchant.settings.lastReceipt', {
                          when: formatDayTime(email.data.last_sent_at, row.timezone, undefined, t),
                        })
                      : t('merchant.settings.noReceiptsYet')}
                  </Text>
                )}
              </Stack>
            ) : (
              <Text className="text-muted-foreground text-xs">
                {t('merchant.settings.checkingEmail')}
              </Text>
            )}
          </SettingsCard>

          {/* Nothing at all when the restaurant is not verified: a card saying so
              would be a to-do item for something the merchant cannot do — the
              mark is given, not applied for. */}
          {restaurant?.verified_since ? (
            <SettingsCard
              title={t('merchant.settings.verificationTitle')}
              description={t('merchant.settings.verificationHint')}>
              <Row gap="sm" align="center">
                <VerifiedBowl size={18} />
                <Text className="text-sm">
                  {t('merchant.settings.verificationSince', {
                    when: formatMonthYear(restaurant.verified_since, restaurant.timezone, t),
                  })}
                </Text>
              </Row>
            </SettingsCard>
          ) : null}

          {/* Last in the stack, because it is the action a merchant comes here
              for least often, and the only one that reaches past the device in
              front of them. */}
          <SettingsCard
            title={t('merchant.settings.sessions')}
            description={t('merchant.settings.sessionsHint')}>
            <Button
              variant="secondary"
              onPress={() => setConfirmingSignOut(true)}
              accessibilityLabel={t('merchant.settings.signOutEverywhere')}>
              <Text>{t('merchant.settings.signOutEverywhere')}</Text>
            </Button>
          </SettingsCard>

          {/* The foot of the stack: the block list is the read a merchant comes
              to least often, so of the two cards down here it goes last. */}
          {twoColumn ? null : blockedCard}
        </View>
      </View>

      <ConfirmDialog
        visible={confirmingSignOut}
        title={t('merchant.settings.signOutEverywhereTitle')}
        message={t('merchant.settings.signOutEverywhereMessage')}
        confirmLabel={t('merchant.settings.signOutEverywhere')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={signingOut}
        onConfirm={() => void signOutEverywhere()}
        onCancel={() => setConfirmingSignOut(false)}
      />
    </Screen>
  );
}
